import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Flame } from "lucide-react";
import { scrollTo } from "./Navbar";
import { Magnetic, CountUp } from "./fx";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  ["Equipping", false],
  ["People.", false],
  ["Strengthening", false],
  ["Leaders.", false],
  ["Advancing", true],
  ["the Kingdom.", true],
];

const STATS = [
  [40, "+", "Years of Ministry"],
  [3, "", "Churches Pastored"],
  [3, "", "Books Authored"],
];

export default function Hero() {
  const { scrollY } = useScroll();
  const yPortrait = useTransform(scrollY, [0, 800], [0, 110]);
  const go = (e, href) => { e.preventDefault(); scrollTo(href); };

  return (
    <section id="hero" data-testid="hero-section" className="relative z-10 min-h-screen overflow-hidden pt-32 pb-24">
      <div className="absolute inset-0 grid-bg" />
      <span aria-hidden className="pointer-events-none select-none absolute -right-8 top-28 font-display font-extrabold text-[20rem] leading-none text-transparent hidden xl:block" style={{ WebkitTextStroke: "1px rgba(139,92,246,0.12)" }}>TT</span>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <motion.div initial={{ opacity: 0, y: -30, scale: 0.92 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.1, ease: EASE }} className="flex justify-center pb-12">
          <motion.img src="/assets/logo.jpeg" alt="Terrell Turner Ministries" data-testid="hero-logo"
            animate={{ boxShadow: ["0 0 50px rgba(139,92,246,0.3)", "0 0 90px rgba(245,158,11,0.4)", "0 0 50px rgba(139,92,246,0.3)"] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="h-40 sm:h-52 lg:h-60 w-auto rounded-3xl ring-1 ring-white/15" />
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 items-center w-full">
        <div className="lg:col-span-8">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}
            data-testid="hero-overline" className="flex items-center gap-3 font-mono text-sm uppercase tracking-[0.28em] text-violet-300 mb-8">
            <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse-dot" />
            Prophetic Ministry &bull; Kingdom Leadership &bull; Wellness &bull; Development
          </motion.p>

          <h1 data-testid="hero-headline" className="font-display font-extrabold uppercase tracking-[-0.02em] leading-[1.04] text-[clamp(1.5rem,6vw,3.2rem)] text-white">
            {LINES.map(([line, gradient], i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span className={`block ${gradient ? "text-vg" : ""}`} initial={{ y: "110%" }} animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.11, ease: EASE }}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-8 max-w-xl text-lg sm:text-xl text-slate-300 font-normal leading-relaxed">
            Empowering people spiritually, personally, and practically to discover their purpose, develop their gifts,
            and fulfill their God-given potential — ministering to the whole person: spirit, mind, and body.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1.25, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4">
            <Magnetic>
              <a href="#about" data-testid="hero-learn-more-btn" onClick={(e) => go(e, "#about")}
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-amber-400 to-violet-500 text-white text-base font-bold uppercase tracking-[0.12em] shadow-[0_10px_35px_-10px_rgba(139,92,246,0.7)] hover:shadow-[0_10px_45px_-8px_rgba(245,158,11,0.7)] hover:brightness-110 transition-all duration-300">
                Learn More <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Magnetic>
            <Magnetic>
              <a href="#contact" data-testid="hero-connect-btn" onClick={(e) => go(e, "#contact")}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full glass text-slate-100 text-base font-bold uppercase tracking-[0.12em] hover:border-violet-400/60 hover:bg-white/[0.06] transition-all duration-300">
                Connect With Us
              </a>
            </Magnetic>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.5 }}
            data-testid="hero-stats" className="mt-14 flex gap-4 sm:gap-6 flex-wrap">
            {STATS.map(([num, suffix, label], i) => (
              <div key={label} className="glass rounded-2xl px-5 py-4 animate-float" style={{ animationDelay: `${i * 0.8}s` }}>
                <p className="font-display text-3xl sm:text-4xl font-extrabold text-vg"><CountUp to={num} suffix={suffix} /></p>
                <p className="mt-1 font-mono text-xs font-medium uppercase tracking-[0.2em] text-slate-300">{label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-4 relative">
          <motion.div style={{ y: yPortrait }} initial={{ opacity: 0, scale: 0.92, rotate: 2 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: EASE }} className="relative">
            <div aria-hidden className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-violet-600/30 via-transparent to-amber-500/25 blur-2xl" />
            <div aria-hidden className="animate-spin-slow absolute -inset-3 rounded-[2.2rem]" style={{ background: "conic-gradient(from 0deg, transparent 0%, rgba(139,92,246,0.6) 12%, transparent 25%, transparent 50%, rgba(245,158,11,0.6) 62%, transparent 75%)" }} />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10">
              <img src="/assets/prophet-portrait.png" alt="Prophet Terrell Turner" data-testid="hero-portrait"
                className="w-full h-[420px] sm:h-[520px] object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#06070B]/80 via-transparent to-transparent" />
            </div>
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-6 -left-4 sm:-left-6 glass rounded-2xl px-5 py-4 flex items-center gap-3 shadow-[0_15px_40px_-15px_rgba(139,92,246,0.5)]">
              <Flame className="text-amber-400" size={22} />
              <div>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-vg">A Prophetic Voice</p>
                <p className="text-base font-semibold text-slate-100">For Today&rsquo;s Church</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2">
        <span className="font-mono text-xs font-medium uppercase tracking-[0.3em] text-slate-300">Scroll</span>
        <motion.span animate={{ scaleY: [0.2, 1, 0.2], opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block h-10 w-px origin-top bg-gradient-to-b from-violet-400 to-amber-400" />
      </motion.div>
    </section>
  );
}
